import React, { useState, useEffect, useCallback } from "react";
import WordExplorer from "./WordExplorer";
import { Dialog } from "@headlessui/react";

interface Segment {
  segment: string;
  index: number;
  isWordLike: boolean;
  input: string;
}

interface TextComponentProps {
  text: string;
}

function TextComponent({ text }: TextComponentProps) {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [wrappedText, setWrappedText] = useState<string>("");
  const [modalX, setModalX] = useState<number>(0);
  const [modalY, setModalY] = useState<number>(0);
  const [clickedWord, setClickedWord] = useState<string>("");

  const segmentText = useCallback((): void => {
    const input = text;
    const segmenter = new Intl.Segmenter("en", { granularity: "word" });
    const segments = segmenter.segment(input);
    const segmentArray = Array.from(segments).map(
      ({ segment, index, isWordLike, input }) => ({
        segment,
        index,
        isWordLike,
        input,
      })
    );
    setSegments(segmentArray);
  }, [text]);

  useEffect(() => {
    segmentText();
  }, [segmentText]);

  const handleClick = (segment: Segment, event: React.MouseEvent<HTMLSpanElement>): void => {
    const input = text;
    const startIndex = segment.index;
    const endIndex = startIndex + segment.segment.length;
    const wrappedText =
      input.substring(0, startIndex) +
      `|${segment.segment}|` +
      input.substring(endIndex);
    
    setShowModal(true);
    setWrappedText(wrappedText);
    setClickedWord(segment.segment);
    setModalX(event.clientX);
    setModalY(event.clientY);
  };

  const handleCloseModal = (): void => {
    setShowModal(false);
    setWrappedText("");
    setClickedWord("");
    setModalX(0);
    setModalY(0);
  };

  return (
    <div>
      {segments.map((segment, index) => (
        <span
          key={index}
          className={
            segment.isWordLike
              ? "hover:bg-secondary-content cursor-pointer"
              : "cursor-auto"
          }
          onClick={(event) => {
            if (segment.isWordLike) {
              handleClick(segment, event);
            }
          }}
        >
          {segment.segment}
        </span>
      ))}
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
          <div
            className="bg-primary rounded-lg p-8 shadow-xl z-20"
            style={{ left: modalX, top: modalY }}
          >
            <WordExplorer text={wrappedText} clickedWord={clickedWord} onClose={handleCloseModal} />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default TextComponent;
